// FEAT-023: 바우처 출력 영역 — @media print 기반
// - N개 바우처를 page-break-after: always 로 쌓아 한 번의 window.print() 에서 전부 출력
// - qrBox 비율(%) 기반 CSS 배치 — 해상도/스케일 무관
// - 배경 이미지가 있으면 -webkit-print-color-adjust: exact 로 프린터에 색상 강제
// - html2pdf.js 미사용 (사용자가 "Save as PDF" 선택)
// - Portal 사용 이유: @media print 의 body>*:not(.voucher-print-area) 선택자가
//   #root 전체를 숨기므로, print-area 를 body 직계 자식으로 두어야 선택자가 정상 동작함.

import { useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { createPortal } from 'react-dom';
import { DEFAULT_QR_BOX, DEFAULT_TYPE_BOX, pctToCss, deriveSerialLayout } from '../../lib/voucherTemplate';
import { Translate as t } from '../../config/Translate';
// IMP-438: 날짜(전용) 포맷 SSoT — 앱 언어 locale 로 렌더.
import { formatLicenseDateOnly } from '../../lib/licenseHelper';
import './voucherPrint.css';

// 보안: React style 객체는 CSS 이스케이프를 수행하지 않아 backgroundImage 값에
// `)`, `"`, `\n` 등이 포함되면 CSSOM 을 탈출할 수 있음 (PR #183 R6).
// ALLOWED_HOSTS: 서버측 인증된 CDN(rayplatform.com, amazonaws.com, aliyuncs.com)만 허용.
//   확장 시 반드시 서버측 서명 CDN 한정 조건 유지 — 임의 호스트 추가 금지.
const isSafeBackgroundUrl = (value) => {
    if (!value || typeof value !== 'string') return false;
    let parsed;
    try {
        parsed = new URL(value);
    } catch {
        return false;
    }
    if (parsed.protocol !== 'https:') return false;
    const ALLOWED_HOSTS = /(^|\.)(rayplatform\.com|amazonaws\.com|aliyuncs\.com)$/i;
    if (!ALLOWED_HOSTS.test(parsed.hostname)) return false;
    if (/[)"'\s\\]/.test(value)) return false;
    return true;
};

// SVG viewBox auto-fit 텍스트 렌더러.
// SAFE_CHAR_W=0.6: 모노스페이스 기준 문자 폭 추정값. viewBox 폭을 텍스트 길이에 비례 설정.
// yFactor: top-aligned(typeBox) = 0.7, vertically-centered(serial) = 0.5.
// dy: 세로 중앙 정렬 시 baseline 보정 "0.33em", top 정렬 시 "0".
const renderFitText = ({
    boxCss,
    text,
    preserveAspectRatio,
    yFactor,
    dy,
    fontWeight,
    fontFamily,
    role,
    ariaLabel,
}) => {
    if (!text) return null;
    const SAFE_CHAR_W = 0.6;
    const FONT_UNIT = 10;
    const VB_W = text.length * FONT_UNIT * SAFE_CHAR_W;
    const VB_H = FONT_UNIT * 1.2;
    return (
        <svg
            role={role}
            aria-label={ariaLabel}
            style={boxCss}
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            preserveAspectRatio={preserveAspectRatio}
        >
            <text
                x={VB_W / 2}
                y={VB_H * yFactor}
                dy={dy}
                textAnchor="middle"
                fontSize={FONT_UNIT}
                fontWeight={fontWeight}
                fontFamily={fontFamily}
                fill="#000"
            >
                {text}
            </text>
        </svg>
    );
};

// IMP-106: orientation 의존 동적 @page 만 인라인 주입. 정적 규칙은 voucherPrint.css.
// 사이드바 숨김: JS useEffect 로 display:none (z-index 와 무관하게 확실히 차단).
// @media screen/@media print 정적 규칙은 voucherPrint.css 로 분리됨.
export const buildDynamicPageStyle = (orientation) => {
    const pageSize = orientation === 'landscape' ? 'A4 landscape' : 'A4';
    return `@media print { @page { size: ${pageSize}; margin: 0; } }`;
};

const VoucherPrintArea = ({ vouchers = [], template }) => {
    // 마운트 시 body 의 다른 직계 자식(사이드바 포함)을 JS 로 직접 display:none.
    // z-index 는 브라우저 인쇄 패널 뒤의 web 요소를 숨기지 못하므로 display 방식 사용.
    useEffect(() => {
        const others = [...document.body.children].filter(
            el => !el.classList.contains('voucher-print-area')
        );
        const saved = others.map(el => el.style.getPropertyValue('display'));
        others.forEach(el => el.style.setProperty('display', 'none', 'important'));
        return () => {
            others.forEach((el, i) => {
                if (saved[i]) {
                    el.style.setProperty('display', saved[i]);
                } else {
                    el.style.removeProperty('display');
                }
            });
        };
    }, []);

    const qrBox = template?.qrBox || DEFAULT_QR_BOX;
    const typeBox = template?.typeBox || DEFAULT_TYPE_BOX;
    const typeBoxCss = pctToCss(typeBox);

    // orientation — serial 실제 크기 계산에 필요하므로 먼저 결정
    const orientation = template?.orientation === 'landscape' ? 'landscape' : 'portrait';
    const isLandscape = orientation === 'landscape';
    const pageWidth = isLandscape ? '297mm' : '210mm';
    const pageHeight = isLandscape ? '210mm' : '297mm';
    const pageWidthMm = isLandscape ? 297 : 210;
    const pageHeightMm = isLandscape ? 210 : 297;
    const printStyles = buildDynamicPageStyle(orientation);

    // BUG-097: voucherTemplate.deriveSerialLayout 으로 serial/QR 렌더 박스를 위임 계산.
    // QR 는 1:1 정사각 → render box short-side 가 실제 이미지 크기. serial 폭/X 를 맞춤.
    const { qrRenderBox, serialBox } = deriveSerialLayout(qrBox, { pageWidthMm, pageHeightMm });
    const qrBoxCss = pctToCss(qrRenderBox);
    const serialBoxCss = pctToCss(serialBox);

    const rawBgUrl = template?.backgroundImageUrl || null;
    const safeBgUrl = rawBgUrl && isSafeBackgroundUrl(rawBgUrl) ? rawBgUrl : null;
    if (rawBgUrl && !safeBgUrl) {
        console.warn('[VoucherPrintArea] rejecting unsafe backgroundImageUrl', rawBgUrl);
    }

    const rawBox = template?.bgBox;
    const bgBox = (rawBox && typeof rawBox === 'object')
        ? {
            xPct: Math.max(0, Math.min(100, Number(rawBox.xPct) || 0)),
            yPct: Math.max(0, Math.min(100, Number(rawBox.yPct) || 0)),
            widthPct: Math.max(1, Math.min(100, Number(rawBox.widthPct) || 100)),
            heightPct: Math.max(1, Math.min(100, Number(rawBox.heightPct) || 100)),
        }
        : { xPct: 0, yPct: 0, widthPct: 100, heightPct: 100 };

    const portalTarget = typeof document !== 'undefined' ? document.body : null;
    const content = (
        <>
            <style>{printStyles}</style>
            <div className="voucher-print-area">
                {vouchers.map((v, idx) => (
                    <div
                        key={v.sk || v.token || `voucher-${idx}`}
                        className="voucher-print-page"
                        style={{
                            position: 'relative',
                            width: pageWidth,
                            height: pageHeight,
                            backgroundColor: '#fff',
                            overflow: 'hidden',
                        }}
                    >
                        {safeBgUrl && (
                            <img
                                src={safeBgUrl}
                                alt=""
                                style={{
                                    position: 'absolute',
                                    left: `${bgBox.xPct}%`,
                                    top: `${bgBox.yPct}%`,
                                    width: `${bgBox.widthPct}%`,
                                    height: `${bgBox.heightPct}%`,
                                    objectFit: 'fill',
                                    display: 'block',
                                }}
                            />
                        )}
                        {v.token && (
                            // QR SVG 1:1 → short side 에 맞춤. flex-end 로 QR 하단을 시리얼 상단에 밀착.
                            <div style={{
                                ...qrBoxCss,
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                            }}>
                                <QRCodeSVG
                                    value={v.qrUrl || v.token}
                                    style={{
                                        height: '100%',
                                        width: 'auto',
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                    }}
                                />
                            </div>
                        )}
                        {/* 라이선스 type 라벨.
                            xMidYMin meet: typeBox 는 QR 바로 아래 좁은 영역이므로 텍스트를 상단에 고정.
                            xMidYMid 사용 시 세로 중앙에 렌더되어 시리얼과 시각적으로 멀어짐.
                            yFactor=0.7: top-aligned 상태에서 baseline 을 시각 중심에 보정. */}
                        {(() => {
                            const isRenewal = v.type === 'renewal' || v.type === 'renew';
                            const typeLabel = isRenewal ? t('license.label.renewal') : t('license.label.initial');
                            // IMP-438: 이 화면은 validLabel 이 빈 문자열('')일 때 아래 typeText 분기
                            // (typeLabel 단독 표시)에 의존한다. 폴백을 SSoT 인자로 넘겨 받는다 —
                            // 종전처럼 `=== '-'` 로 되묻으면 SSoT 의 폴백 표현이 바뀔 때
                            // 인쇄물에 '-' 가 그대로 노출된다(PR #551 리뷰).
                            const validLabel = formatLicenseDateOnly(v.validuntil, { fallback: '' });
                            const typeText = validLabel
                                ? `${typeLabel} ${t('license.field.validUntil')}: ${validLabel}`
                                : typeLabel;
                            return renderFitText({
                                boxCss: typeBoxCss,
                                text: typeText,
                                preserveAspectRatio: 'xMidYMin meet',
                                yFactor: 0.7,
                                dy: '0',
                                fontWeight: 'bold',
                                fontFamily: undefined,
                            });
                        })()}
                        {/* BUG-097: Serial — SVG viewBox auto-fit 으로 박스 안에 정확히 맞춤.
                            serial 박스 폭 = QR 실제 이미지 폭(deriveSerialLayout 위임) → 과도한 확대 없음.
                            dy="0.33em": SVG baseline 이 y 중심에 위치할 때 시각 중심 보정값.
                            token 은 SPEC-RG-05 상 QR 불가 시 수동 입력용 공개 시리얼 — 별도 마스킹 불필요. */}
                        {v.token && renderFitText({
                            boxCss: serialBoxCss,
                            text: v.token,
                            preserveAspectRatio: 'xMidYMid meet',
                            yFactor: 0.5,
                            dy: '0.33em',
                            fontWeight: undefined,
                            fontFamily: 'monospace',
                            role: 'img',
                            ariaLabel: `${t('license.field.serial')}: ${v.token}`,
                        })}
                    </div>
                ))}
            </div>
        </>
    );
    return portalTarget ? createPortal(content, portalTarget) : content;
};

export default VoucherPrintArea;
